/** Capture receipt DOM to PNG and optionally share via Web Share / WhatsApp. */
export async function captureReceiptPng(
  source: HTMLElement,
  opts?: { width?: number; fileName?: string },
): Promise<{ blob: Blob; dataUrl: string; fileName: string }> {
  const html2canvas = (await import('html2canvas')).default;
  const width = opts?.width || 680;
  const fileName = opts?.fileName || 'Receipt.png';

  const mount = document.createElement('div');
  mount.className = 'invoice-wrapper';
  mount.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;z-index:-1;pointer-events:none;background:#ffffff;padding:0;margin:0;`;

  // Copy document stylesheet links and style tags so clone retains full fidelity
  const docStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((s) => s.cloneNode(true));
  docStyles.forEach((s) => mount.appendChild(s));

  const clone = source.cloneNode(true) as HTMLElement;
  clone.style.width = `${width}px`;
  clone.style.maxWidth = `${width}px`;
  clone.style.overflow = 'visible';

  const forceStyle = document.createElement('style');
  forceStyle.textContent = `
    .invoice-wrapper table.items, table.items { display: table !important; width: 100% !important; }
    .invoice-wrapper .items-mobile, .items-mobile { display: none !important; }
    .invoice-wrapper, .invoice-wrapper .invoice, .invoice { overflow: visible !important; max-height: none !important; box-shadow: none !important; }
  `;
  mount.appendChild(forceStyle);
  mount.appendChild(clone);
  document.body.appendChild(mount);

  try {
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      windowWidth: Math.max(clone.scrollWidth, width),
      windowHeight: clone.scrollHeight,
    });
    const dataUrl = canvas.toDataURL('image/png');
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG failed'))), 'image/png');
    });
    return { blob, dataUrl, fileName };
  } finally {
    mount.remove();
  }
}

export function downloadDataUrl(dataUrl: string, fileName: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function shareReceiptViaWhatsApp(opts: {
  source: HTMLElement;
  invoiceName?: string;
  totalLabel?: string;
  customerMobile?: string;
  width?: number;
}): Promise<'shared' | 'downloaded_whatsapp' | 'downloaded'> {
  return shareReceiptImage(opts);
}

export async function shareReceiptImage(opts: {
  source: HTMLElement;
  invoiceName?: string;
  totalLabel?: string;
  customerMobile?: string;
  width?: number;
}): Promise<'shared' | 'downloaded_whatsapp' | 'downloaded'> {
  const fileName = `Receipt-${opts.invoiceName || 'invoice'}.png`;
  const { blob, dataUrl } = await captureReceiptPng(opts.source, {
    width: opts.width ?? 680,
    fileName,
  });
  const file = new File([blob], fileName, { type: 'image/png' });
  const text = `Receipt ${opts.invoiceName || ''}${opts.totalLabel ? ` — ${opts.totalLabel}` : ''}`.trim();

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: fileName, text });
      return 'shared';
    } catch (err: any) {
      if (err?.name === 'AbortError') return 'shared';
    }
  }

  downloadDataUrl(dataUrl, fileName);

  let phone = (opts.customerMobile || '').replace(/\D/g, '');
  if (phone.startsWith('0') && phone.length === 11) phone = `92${phone.slice(1)}`;
  const msg = encodeURIComponent(
    `${text}\nI've downloaded the receipt image — please attach the PNG from your device.`,
  );
  const wa = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  window.open(wa, '_blank');
  return 'downloaded_whatsapp';
}
